import { Box } from "@mui/material"
import React, { useContext, useMemo } from "react"
import { RouteChildrenProps, useLocation } from "react-router"
import { CustomIcon, FaIcon } from "../../../../mp-core/icons"
import {
  NavPills,
  NavPillsType
} from "../../../../mp-shared-components/generic-shared-components/components"
import { getWorkkeysReplaceTerm } from "../../../../reporting/helpers"
import { EocRouteParams } from "../../types"
import { GlobalMachineContext } from "./StudyPlansInit"
import { useStyles } from "./styles"
import { FeatureFlagContext } from "../../../../mp-core/FeatureFlagProvider"
import { FeatureFlagName } from "../../../../mp-utils/generic-utils/feature-flags/types"

type Props = RouteChildrenProps & {
  program: string
  subject: string
  isOldNav: boolean
}

const StudyPlanSubjectContainer: React.FC<Props> = ({
  program,
  subject,
  isOldNav,
  children,
  ...props
}) => {
  const { classes } = useStyles()

  const { search, pathname } = useLocation()

  const { featureFlags } = useContext(FeatureFlagContext)

  const { machineLoading, scoresByStudentLoading } =
    GlobalMachineContext.useSelector(state => ({
      machineLoading: state.context.loading,
      scoresByStudentLoading: state.matches(
        "study_plans_scores.get_scores_by_student"
      )
    }))

  // Construct tab navigation paths
  const eocParams = isOldNav ? (props?.match?.params as EocRouteParams) : null
  const currentSubject = eocParams?.subject ?? subject

  // Determine if we should show tabs (ReadOnlyStudyPlanPage or scores pages only)
  // Show tabs when on base subject route OR scores route, but NOT on /view, /edit, /preview
  const showTabs =
    pathname.includes("/scores") ||
    (!pathname.includes("/view") &&
      !pathname.includes("/edit") &&
      !pathname.includes("/preview") &&
      currentSubject)

  // Progress tab should go to base study-plan route (no subpage)
  const baseProgressPath = isOldNav
    ? `/eoc/${program?.toLowerCase()}/study-plans/${currentSubject}${search}`
    : `/reporting/teaching-tools/study-plan${search}`

  const scoresPath = isOldNav
    ? `/eoc/${program?.toLowerCase()}/study-plans/${currentSubject}/scores${search}`
    : `/reporting/teaching-tools/study-plan/scores${search}`

  const tabsDisabled = useMemo(() => {
    return machineLoading || scoresByStudentLoading
  }, [machineLoading, scoresByStudentLoading])

  const tabItems: NavPillsType[] = useMemo(() => {
    const tabs = [
      {
        label: "Progress",
        value: "progress",
        href: baseProgressPath,
        iconPosition: "start",
        icon: <CustomIcon name="bar-progress" />,
        disabled: tabsDisabled,
        dataAutomationId: "study-plan-progress-tab"
      }
    ]

    // Only add Scores tab if feature flag is enabled
    if (featureFlags?.[FeatureFlagName.study_plan_scores_by_student_module]) {
      tabs.push({
        label: `Scores By ${getWorkkeysReplaceTerm("student", true)}`,
        value: "scores",
        href: scoresPath,
        iconPosition: "start",
        icon: <FaIcon solid name="user" />,
        disabled: tabsDisabled,
        dataAutomationId: "study-plan-scores-by-student-tab"
      })
    }

    return tabs
  }, [baseProgressPath, scoresPath, tabsDisabled, featureFlags])

  const currentTab = pathname.includes("/scores") ? "scores" : "progress"

  return (
    <>
      {showTabs && currentSubject && (
        <Box className={classes.studyPlanTabsContainer}>
          <NavPills
            size="sm"
            mode="dark"
            variant="outlined"
            items={tabItems}
            value={currentTab}
            tabClasses={{ root: classes.studyPlanTabs }}
            dataAutomationId="study-plan-tabs"
          />
        </Box>
      )}
      {children}
    </>
  )
}

export default StudyPlanSubjectContainer
